import type { CSSProperties } from "react";
import { VARIANT, type VariantId } from "../../lib/tokens";

export function SkeletonCard({ variant }: { variant: VariantId }) {
  const lineColor = variant === "immersive" ? "#222" : "#e8e6df";

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
      aria-hidden="true"
    >
      <div
        style={{
          aspectRatio: "3 / 4",
          background: lineColor,
          borderRadius: 14,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Shimmer />
      </div>
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 4 }}>
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            background: lineColor,
            flexShrink: 0,
          }}
        />
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
          <div
            style={{
              height: 10,
              width: "70%",
              background: lineColor,
              borderRadius: 4,
            }}
          />
          <div
            style={{
              height: 8,
              width: "50%",
              background: lineColor,
              borderRadius: 4,
              opacity: 0.6,
            }}
          />
        </div>
      </div>
    </div>
  );
}

export function SkeletonCategoryChips({ variant }: { variant: VariantId }) {
  const v = VARIANT[variant];
  const lineColor = variant === "immersive" ? "#222" : "#e8e6df";
  return (
    <div
      style={{
        display: "flex",
        gap: 8,
        padding: "14px 32px",
        borderBottom: `1px solid ${v.border}`,
        overflow: "hidden",
      }}
    >
      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={i}
          style={{
            height: 28,
            width: 60 + (i % 3) * 30,
            borderRadius: 999,
            background: lineColor,
            flexShrink: 0,
          }}
          aria-hidden="true"
        />
      ))}
    </div>
  );
}

export function SkeletonHero({ variant }: { variant: VariantId }) {
  const v = VARIANT[variant];
  const lineColor = variant === "immersive" ? "#222" : "#e8e6df";
  const surfaceBg = v.surface;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1.4fr 1fr",
        gap: 40,
        alignItems: "center",
        padding: 32,
        marginTop: 8,
        marginBottom: 16,
        background: surfaceBg,
        borderRadius: 20,
        border: `1px solid ${v.border}`,
        minHeight: 380,
      }}
      aria-hidden="true"
    >
      <div>
        <div
          style={{
            height: 22,
            width: 160,
            background: lineColor,
            borderRadius: 999,
            marginBottom: 20,
          }}
        />
        <div
          style={{
            height: 48,
            width: "90%",
            background: lineColor,
            borderRadius: 6,
            marginBottom: 12,
          }}
        />
        <div
          style={{
            height: 48,
            width: "60%",
            background: lineColor,
            borderRadius: 6,
            marginBottom: 24,
          }}
        />
        <div style={{ display: "flex", gap: 10 }}>
          <div
            style={{
              height: 12,
              flex: 1,
              background: lineColor,
              borderRadius: 4,
            }}
          />
        </div>
      </div>
      <div
        style={{
          aspectRatio: "3 / 4",
          background: lineColor,
          borderRadius: 16,
          maxHeight: 380,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Shimmer />
      </div>
    </div>
  );
}

function Shimmer() {
  const style: CSSProperties = {
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent)",
    animation: "scrolly-shimmer 1.6s linear infinite",
    willChange: "transform",
  };
  return <div style={style} />;
}
